import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  clientApi,
  RetailerFieldDefinition,
  RetailerLabelType,
  RetailerTemplateDimensions,
} from '../../api/client';
import { EmptyState } from '../../components/EmptyState';
import { SkeletonTable } from '../../components/SkeletonTable';

const LABEL_TYPE_BADGE_STYLES: Record<RetailerLabelType, string> = {
  shipping: 'bg-blue-100 text-blue-800',
  pallet: 'bg-amber-100 text-amber-800',
  carton: 'bg-emerald-100 text-emerald-800',
  box: 'bg-violet-100 text-violet-800',
  innerpack: 'bg-cyan-100 text-cyan-800',
  placard: 'bg-rose-100 text-rose-800',
  custom: 'bg-slate-100 text-slate-800',
};

export function TemplateDetailPage() {
  const { retailerId, templateKey } = useParams<{ retailerId: string; templateKey: string }>();
  const retailerLink = retailerId ? `/retailers/${retailerId}` : '/retailers';

  const { data: template, isLoading, error } = useQuery({
    queryKey: ['client', 'retailer-template', retailerId, templateKey],
    queryFn: () => clientApi.getRetailerTemplateDoc(retailerId!, templateKey!),
    enabled: !!retailerId && !!templateKey,
  });

  if (isLoading) {
    return <TemplateDetailSkeleton />;
  }

  if (error || !template) {
    return (
      <div className="space-y-4">
        <Link to={retailerLink} className="text-sm font-medium text-primary-600 hover:text-primary-800">
          ← Back to Retailer
        </Link>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <h1 className="text-lg font-semibold text-red-900">Template not found</h1>
          <p className="mt-1 text-sm text-red-700">
            {error instanceof Error ? error.message : 'Could not load template details.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link to={retailerLink} className="text-sm font-medium text-primary-600 hover:text-primary-800">
        ← Back to Retailer
      </Link>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{template.displayName}</h1>
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${LABEL_TYPE_BADGE_STYLES[template.labelType]}`}
            >
              {formatSlug(template.labelType)}
            </span>
          </div>
          <p className="mt-2 font-mono text-sm text-gray-500">{template.templateKey}</p>
          <p className="mt-3 text-sm text-gray-600">{formatDimensions(template.dimensions)}</p>
        </div>

        <Link
          to={{ pathname: '/test-label', search: `?template=${encodeURIComponent(template.templateKey)}` }}
          className="btn btn-primary whitespace-nowrap"
        >
          Generate Test Label
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900">Overview</h2>
            <dl className="mt-4 space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Purpose</dt>
                <dd className="mt-1 text-sm text-gray-700">{template.purpose}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">When To Use</dt>
                <dd className="mt-1 text-sm text-gray-700">{template.whenToUse}</dd>
              </div>
              {template.whenNotToUse && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">When Not To Use</dt>
                  <dd className="mt-1 text-sm text-gray-700">{template.whenNotToUse}</dd>
                </div>
              )}
            </dl>
          </div>

          <FieldsTable
            title="Required Fields"
            fields={template.requiredFields}
            emptyTitle="No required fields documented"
            emptyDescription="This template does not list any required fields yet."
          />

          <FieldsTable
            title="Optional Fields"
            fields={template.optionalFields}
            emptyTitle="No optional fields documented"
            emptyDescription="This template does not list any optional fields yet."
          />
        </div>

        <div className="space-y-6">
          {template.previewImage && (
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900">Preview</h2>
              <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                <img
                  src={template.previewImage}
                  alt={`${template.displayName} preview`}
                  className="h-auto w-full object-contain"
                />
              </div>
            </div>
          )}

          {template.specReference && (
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900">Specification Reference</h2>
              <p className="mt-3 text-sm text-gray-700">{template.specReference}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldsTable({
  title,
  fields,
  emptyTitle,
  emptyDescription,
}: {
  title: string;
  fields?: RetailerFieldDefinition[];
  emptyTitle: string;
  emptyDescription: string;
}) {
  return (
    <div className="card overflow-hidden p-0">
      <div className="border-b border-gray-200 px-6 py-4">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      </div>

      {!fields || fields.length === 0 ? (
        <div className="p-6">
          <EmptyState icon="🧾" title={emptyTitle} description={emptyDescription} />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  Field
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  Rules
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {fields.map((field) => (
                <tr key={field.name} className="align-top hover:bg-gray-50">
                  <td className="px-6 py-4 font-mono text-sm text-gray-900">{field.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{field.type}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{field.description}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    <div className="space-y-1">
                      {field.maxLength !== undefined && <div>Max length: {field.maxLength}</div>}
                      {field.validation && <div className="font-mono text-xs text-gray-500">{field.validation}</div>}
                      {field.maxLength === undefined && !field.validation && <div className="text-gray-400">-</div>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TemplateDetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-3">
          <div className="h-8 w-64 animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-48 animate-pulse rounded bg-gray-100" />
          <div className="h-4 w-36 animate-pulse rounded bg-gray-100" />
        </div>
        <div className="h-10 w-40 animate-pulse rounded bg-gray-200" />
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <div className="card animate-pulse space-y-4">
            <div className="h-6 w-32 rounded bg-gray-200" />
            <div className="h-4 w-full rounded bg-gray-100" />
            <div className="h-4 w-5/6 rounded bg-gray-100" />
            <div className="h-4 w-full rounded bg-gray-100" />
            <div className="h-4 w-4/5 rounded bg-gray-100" />
          </div>
          <div className="card p-0">
            <div className="border-b border-gray-200 px-6 py-4">
              <div className="h-6 w-36 animate-pulse rounded bg-gray-200" />
            </div>
            <SkeletonTable rows={6} columns={4} />
          </div>
          <div className="card p-0">
            <div className="border-b border-gray-200 px-6 py-4">
              <div className="h-6 w-36 animate-pulse rounded bg-gray-200" />
            </div>
            <SkeletonTable rows={4} columns={4} />
          </div>
        </div>
        <div className="card animate-pulse">
          <div className="h-6 w-20 rounded bg-gray-200" />
          <div className="mt-4 h-80 rounded-lg bg-gray-100" />
        </div>
      </div>
    </div>
  );
}

function formatDimensions(dimensions: RetailerTemplateDimensions): string {
  return `${dimensions.width} x ${dimensions.height} ${dimensions.unit} @ ${dimensions.dpi} DPI`;
}

function formatSlug(value: string): string {
  return value
    .split('-')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}
